import { IconButton, Paper, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from "@mui/material";
import { Check, Delete, Edit } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { APICallStatus, DataTypes, UserType, Violation, ViolationStatus, ViolationType } from "../../Types/UnitFeatureTypes";
import { useAuth } from "../../context/authContext";
import { useDispatch, useSelector } from "react-redux";
import { Actions } from "../../Redux/Reducer/rootReducer";
import { statusUpdateViolationAPIStatus } from "../../Redux/selectors";
import { useEffect } from "react";
import { useApp } from "../../context/appContext";

interface BuildViolationsTableProps {
    item: Violation[];
    showActions?: boolean;
}

const BuildViolationsTable: React.FC<BuildViolationsTableProps> = ({
    item,
    showActions = true
}) => {

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { user } = useAuth();
    const IS_MANAGER = user?.type === UserType.MANAGER;
    const statusUpdateApi = useSelector(statusUpdateViolationAPIStatus);
    const { setSnackbar } = useApp();


    useEffect(() => {
        if (statusUpdateApi === APICallStatus.SUCCESS) {
            dispatch(Actions.fetchViolation.violationActions.request({}));
            setSnackbar(true, 'Violation status updated successfully', 'success');
        } else if (statusUpdateApi === APICallStatus.FAILURE) {
            setSnackbar(true, 'Failed to update violation status', 'error');
        }
        dispatch(Actions.violation.resetStatusRequestAction());
    }, [statusUpdateApi]);

    const handleViolationExpand = (id: number) => {
        navigate(`/viewViolation`, { state: { id: id, type: DataTypes.VIOLATION } });
    }

    const handleEdit = (violation: Violation) => {
        navigate('/createViolation', { state: { edit: true, violation: violation } });
    }

    const handleDelete = (violation: Violation) => {
        // navigate('/createAnnouncement', { state: { copy: true, announcement: announcement } });
    }

    const getTableData = (item: Violation[]) => {
        return (<Table>
            <TableHead>
                <TableRow>
                    <TableCell sx={{fontWeight: 600}} align="center" >Date</TableCell>
                    <TableCell sx={{fontWeight: 600}} align="center">Title</TableCell>
                    <TableCell sx={{fontWeight: 600}} align="center">Stage</TableCell>
                    <TableCell sx={{fontWeight: 600}} align="center">Type</TableCell>
                    <TableCell sx={{fontWeight: 600}} align="center">Due Date</TableCell>
                    <TableCell sx={{fontWeight: 600}} align="center">Status</TableCell>
                    {showActions && IS_MANAGER && <TableCell sx={{fontWeight: 600}} align="center">Quick Actions</TableCell>}
                </TableRow>
            </TableHead>
            <TableBody>
                {item.map((violation: Violation) =>
                    <TableRow key={violation.id} sx={{'&:hover': {
                        backgroundColor: '#f5f5f5',
                        cursor: 'pointer'
                    }}} onClick={(e) => {
                            if ((e.target as HTMLElement).closest('#actions')) return;
                            handleViolationExpand(violation.id)
                        }}>
                        <TableCell align="center">{new Date(violation.created_on).toDateString()}</TableCell>
                        <TableCell sx={{maxWidth: 100}} align="center">
                            <Tooltip title={violation.title}>
                                <Typography variant="body2" color="textSecondary" sx={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {violation.title}
                                </Typography>
                            </Tooltip>
                        </TableCell>
                        <TableCell sx={{maxWidth: 400}} align="center">
                            <Tooltip title={violation.stage}>
                                <Typography variant="body2" color="textSecondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {violation.stage}
                                </Typography>
                            </Tooltip>
                        </TableCell>
                        <TableCell align="center">
                            <Tooltip title={`${violation.type}${violation.type === ViolationType.OTHER ? ': ' + violation.other_type : ''}`}>
                                <Typography variant="body2" color="textSecondary">{violation.type + (violation.type === ViolationType.OTHER ? ': ' + violation.other_type : '')}</Typography>
                            </Tooltip>
                        </TableCell>
                        <TableCell align="center">
                            {violation.due_date ? <Tooltip title={new Date(violation.due_date).toDateString()}>
                                <Typography variant="body2" color="textSecondary">{new Date(violation.due_date).toDateString()}</Typography>
                            </Tooltip>: <Typography variant="body2" color="textSecondary">No Due Date</Typography>}
                        </TableCell>
                        <TableCell align="center">
                            <Typography variant="body2" color="textSecondary">{violation.status}</Typography>
                        </TableCell>
                        {showActions && IS_MANAGER && <TableCell align="center" id="actions">
                            {(violation.status === ViolationStatus.OPEN || violation.status === ViolationStatus.OWNER_ACTION_REQUIRED)  && <Tooltip title="Resolve">
                                <IconButton onClick={() => handleResolveViolation(violation.id)}><Check /></IconButton> 
                            </Tooltip>}
                            {violation.status === ViolationStatus.DRAFT && <>
                                <Tooltip title="Edit Violation">
                                    <IconButton onClick={() => handleEdit(violation)}><Edit /></IconButton> 
                                </Tooltip>
                                <Tooltip title="Delete Draft">
                                    <IconButton onClick={() => handleDelete(violation)}><Delete /></IconButton> 
                                </Tooltip>
                            </>}
                        </TableCell>}
                    </TableRow>
                )}
            </TableBody>
        </Table>)
    }

    if (!item.length) {
        return (
            <Paper elevation={3} sx={{borderRadius: 3, padding: 2, alignItems: 'center', display: 'flex', justifyContent: 'center'}}>
                <Typography variant="subtitle1" fontWeight={600} color="textSecondary">No Violation Available</Typography>
            </Paper>
        )
    }

    const handleResolveViolation = (id: number) => {
        dispatch(Actions.violation.updateStatusViolationRequestAction.request({ id, status: ViolationStatus.RESOLVED }));
    }

    return (
        <Paper elevation={3} sx={{borderRadius: 3, overflow: 'auto'}}>
            {getTableData(item)}
        </Paper>
    )
}

export default BuildViolationsTable;